import { useEffect } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { AnimatePresence } from 'framer-motion';
import PropTypes from 'prop-types';

import * as Styled from './AuthModal.style';

/**
 * Base authentication modal wrapper component
 * Provides consistent layout and behavior for all auth modals
 *
 * @param {object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Function} props.onClose - Callback when modal should close
 * @param {import('react').ReactNode} props.children - Modal content
 * @param {string} [props.maxWidth] - Modal max width (default: 'md')
 * @param {string} [props.ariaLabel] - Accessibility label for screen readers
 * @returns {object} The AuthModal component
 */
function AuthModal({ isOpen, onClose, children, maxWidth, ariaLabel }) {
  // Close on Escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleOverlayClick = (event) => {
    // Close only if clicking the overlay itself, not the modal content
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <Styled.AuthModalOverlay
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          initial={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={handleOverlayClick}
        >
          <Styled.AuthModalContainer
            $maxWidth={maxWidth}
            animate={{ scale: 1, opacity: 1 }}
            aria-label={ariaLabel}
            aria-modal="true"
            exit={{ scale: 0.95, opacity: 0 }}
            initial={{ scale: 0.95, opacity: 0 }}
            role="dialog"
            transition={{ duration: 0.2 }}
          >
            {/* Close Button */}
            <Styled.AuthModalCloseButton
              aria-label="Close modal"
              label="Close modal"
              onClick={onClose}
            >
              <CloseIcon fontSize="small" />
            </Styled.AuthModalCloseButton>

            {/* Header with Logo */}
            <Styled.AuthModalHeader>
              <Styled.AuthModalLogo
                alt="ZenBusiness Velo"
                src="/s2/logo/zb-velo-stacked-color.svg"
              />
            </Styled.AuthModalHeader>

            {/* Content Area */}
            <Styled.AuthModalContent>{children}</Styled.AuthModalContent>
          </Styled.AuthModalContainer>
        </Styled.AuthModalOverlay>
      )}
    </AnimatePresence>
  );
}

AuthModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  maxWidth: PropTypes.string,
  ariaLabel: PropTypes.string
};

export default AuthModal;
